<template>
  <v-container>
    <v-row justify="center" class="title my-10 primary--text"><h1>Testing a combustion reaction mechanism</h1></v-row>
    <v-row class="my-5">
    <h1 id="introduction">Introduction</h1>
    </v-row>
    <v-row>
    <p>In combustion chemistry research, it is a common task to test such mechanisms against experimental data.
      Either because you want to assess how published mechanisms are able to reproduce your recent measurements or
      you wish to validate your new or updated mechanism against existing experimental data.
      In this video I am going to show you that using the tools developed in our group, this task can be completed fairly easily.</p>
      <p>There are several widely used combustion simulation codes, like
        CHEMKIN,
        <a href="https://cantera.org/" target="_blank">Cantera</a>,
        <a href="https://www.itv.rwth-aachen.de/downloads/flamemaster/" target="_blank">FlameMaster</a> and
        <a href="https://www.opensmokepp.polimi.it/" target="_blank">OpenSMOKE++</a>, just to name a few.</p>
      <p>
        These codes are all able to simulate the so called canonical combustion experiments, like shock tube, rapid compression machine, perfectly stirred reaction or tubular reactor experiments.
        Also, these codes can calculate laminar burning velocity and species profiles in burner stabilized flames.
        The results of these types of measurements are called indirect experimental data.
      </p>
      <p>
        In other types of measurements the rate coefficients of elementary reactions are determined in a direct way.
        A practical problem is that the simulation of these experiments require different input data files and keywords.
        Also, the format of the data files and the keywords are different in each simulation code.
        This is why we developed a simulation framework Optima++.
        This program interprets a unified input data file and currently it is able to use two different simulation codes.
      </p>
      <p>
        The usual way of testing a detailed reaction mechanism can be a complicated process.
        Experimental conditions and results are collected in Excel sheets,
        input files of the actual simulation code are created and separate runs are started for each experimental data point.
        The results are extracted from the output files of the simulation codes and a separate plotting program is used to evaluate the results.
        These steps are very tedious and many of them have to be repeated manually when something is changed.
      </p>
      <p>
        The offer here is another approach.
        The experimental conditions and measurement results are encoded first in XML datafiles.
        Optima++ can read several XML datafiles in a single step, carries out all the simulations using a selected simulation code,
        reads the output files of the simulation codes, and compares the experimental data and the simulation results.
        The comparison can be in the form of traditional plots, or by calculating an error function value that shows the agreement
        between the experimental data and the simulation results.
      </p>
      <p>
        Professor Michael Franklach at the University of Berkeley was the first who recommended
        the usage of XML datafiles for the storage of combustion experimental data.
        The data were stored using the so called PrIMe Kinetics Data format and large amount of data were published in the <a target="_blank" href="http://www.primekinetics.org">PrIMe web site</a>
        <citation id="Frenklach2007" :references="references" />
        .
      </p>
    </v-row>
    <v-row justify="center">
      <a target="_blank" href="http://www.primekinetics.org">
        <v-img contain max-width="200" :src="primeLogo" :lazy-src="primeLogo" class="text-center"></v-img>
      </a>
    </v-row>
    <v-row class="my-5">
      <h1 id="v1-respecth">ReSpecTh Kinetics Data format</h1>
    </v-row>
    <v-row>
      <p>
        Our group found that some experimental results cannot be encoded in the original PrIMe format,
        therefore we created a modified version.
        This modified XML data format is called ReSpecTh Kinetics Data format (RKD format in short)
        and the latest version is RKD format version 2.2.
        The RKD format files are not compatible with the original PrIMe format data files.
        The exact format of the RKD v2.2 files is defined in this document <citation id="RespecthSpec2_2" :references="references" />.
      </p>
      <div class="dash px-5 mb-3">
          <p class="mb-1">Added text on 1 December, 2020:</p>
          <p class="ml-3 mb-1">
          All data files have been upgraded to
          RKD version 2.3. These datafiles contain more information and are fully
          backward compatible to version 2.2. The exact format of the RKD v2.3
          files is defined in document <citation id="RespecthSpec2_3" :references="references" />.
          </p>
      </div>
      <p>
        XML is the acronym of Extensible Markup Language.
        It is a markup language that defines a set of rules for encoding documents in a format that is both human-readable and machine-readable.
        RKD format files can be produced using program Optima++. However, this is a longer story and it will be the subject of another <router-link to="/videos/video3">video</router-link>.
        In the examples presented here, the RKD format files will be downloaded from the <a target="_blank" href="http://respecth.chem.elte.hu/respecth/">ReSpecTh Information Site</a>.
      </p>
    </v-row>
    <v-row justify="center" class="my-4">
      <a target="_blank" href="http://respecth.hu">
      <v-img :src="require('@/assets/respecth-screen.png')" :lazy-src="require('@/assets/respecth-screen.png')" max-width="800" />
      </a>
    </v-row>
    <v-row>
      <p>
        ReSpecTh is an information system that was created and is maintained in the collaboration of two research groups in the Institute of Chemistry of ELTE.
        Anyone may use ReSpecTh free after a <a target="_blank" href="http://respecth.chem.elte.hu/respecth/register_tobe.php">registration</a>.
        For the registration, providing an institutional e-mail address is required.
      </p>
      <p>
        The ReSpecTh information system deals with three related areas:
        reaction kinetics, spectroscopy and thermodynamics and accordingly has three branches abbreviated with Re, Spec, and Th.
      </p>
    </v-row>
    <v-row class="my-5">
      <h1 id="v1-download">Downloading XML datafiles from ReSpecTh.hu</h1>
    </v-row>
    <v-row>
      <p>
        In the following steps, I will show how to look up and download methanol ignition delay data measured in a shock tube from the ReSpecTh site.
      </p>
      <p>
        After clicking on the <code>reaction kinetics</code> branch, you have to provide a username and password to see the content.
      </p>
    </v-row>
    <v-row class="my-4">
      <v-col>
        <image-viewer :src="require('@/assets/respecth-login.png')" :small-width="500" />
      <p>
        After logging in, we will go to the <a target="_blank" href="http://respecth.chem.elte.hu/respecth/reac/experiment.php"><code>indirect experimental data</code></a> page.
      </p>
      <image-viewer :src="require('@/assets/respecth-indir.png')" :small-width="500" />
      <p>
        In this page, we can search in the database using several searching criteria (for example species in the initial mixture, temperature, pressure or bibliographical information).
      </p>
      <image-viewer :src="require('@/assets/respecth-search.png')" :small-width="500" />
      <p>
        After clicking on the <code>search</code> button, the matching xml files will be listed in a table.
      </p>
      <image-viewer :src="require('@/assets/respecth-results.png')" :small-width="500" />
      <p>
        If you click on the name of an XML file, the web browser will show the content of the file in a human readable form.
        You can save this file using the <code>save as</code> in the right click context menu.
      </p>
      <image-viewer :src="require('@/assets/respecth-xml.png')" :small-width="500" />
        <p>Now let us have a closer look at the content of an RKD file:</p>
        <highlight-code lang="xml" class="code-block mb-6" :code="rkdExample" />
        <p>
          An RKD file stores the information between starting and ending tags.
          For example, the information about a physical property of the experiments should be defined inside a
          <highlight-code lang="xml" class="code-block" inline code="<property>" />
          tag.
          The actual name of the property should be given with the
          <highlight-code lang="xml" class="code-block" inline code="name" />
          attribute.
          In this case, the <highlight-code lang="xml" class="code-block" inline code='"initial composition"' />
          property contains nested
          <highlight-code lang="xml" class="code-block" inline code="<component>" />
          elements and the number between the
          <highlight-code lang="xml" class="code-block" inline code="<amount>" />
          tags is the amount of the corresponding species in the initial mixture,
          while the
          <highlight-code lang="xml" class="code-block" inline code='<... units="mole fraction"' />
          attribute means that the unit of the composition is mole fraction.
        </p>
      </v-col>
    </v-row>
    <v-row class="my-5">
      <h1 id="v1-mechanism">Downloading mechanism files from ReSpecTh</h1>
    </v-row>
    <v-row>
      <v-col>
        <p>Now let us go back to the ReSpecTh site and navigate to the <a target="_blank" href="http://respecth.chem.elte.hu/respecth/reac/mechanism.php">MECHANISMS</a> page.</p>
        <image-viewer :src="require('@/assets/respecth-mech.png')" :small-width="500" />
        <p>
          We may look for a mechanism under the <code>Collection of recent methanol combustion mechanisms</code> tab.
          In this example, we will use the Li-2007 mechanism.
        </p>
        <image-viewer :src="require('@/assets/respecth-mech-download.png')" :small-width="500" />
      </v-col>
    </v-row>
    <v-row class="my-5">
      <h1 id="v1-optima">Running Optima++</h1>
    </v-row>
    <v-row>
      <v-col>
        <p>
          In the following steps we will use computer program Optima++<citation id="OptimaPP" :references="references" />. If you click on the <code>computer programs</code> page, the latest release is available under the <code>Optima++</code> tab.
        </p>
       <image-viewer :src="require('@/assets/respecth-optima.png')" :small-width="500" />
        <p>
          Optima++ is a framework program. This means that it organizes the input and output files,
          and controls the parallel running of the simulation codes on a computer cluster.
          Optima++ does not include a simulation code and it uses external simulation codes.
          Currently, Optima++ may use simulation codes FlameMaster <citation id="flamemaster" :references="references" /> and OpenSMOKE++ <citation id="opensmoke" :references="references"/>.
          These are freely available codes that can be downloaded from the web sites of the
          <a target="_blank" href="https://www.itv.rwth-aachen.de/downloads/flamemaster/">RWTH Aachen University</a> and
          the <a target="_blank" href="https://www.opensmokepp.polimi.it/">Politecnico di Milano</a>, respectively.
        </p>
        <extra-bits id="bit-optima-install" title="How to install Optima++?" :markdown="optimaInstall" />
        <p>
          The downloaded mechanism and xml files have been copied into the <code>mechanisms</code> and <code>xmls</code> folders, respectively, in my Optima++ installation.
        </p>
        <image-viewer :src="require('@/assets/opp-files.png')" :small-width="500" />
        <p>You can download these files from here:</p>
        <download-box title="download example files" file="video-1-example-files.zip" />
        <p>
          To set up a mechanism testing job, click on the <code>simulate experimental data</code> button.
        </p>
        <image-viewer :src="require('@/assets/opp-start.png')" :small-width="500" />
        <p>
          In this window, we can specify the parameters of our mechanism testing (MechTest) job.
          First, we can browse the mechanism file by clicking on the folder button at the end of the <code>Mechanism</code> line.
        </p>
        <image-viewer :src="require('@/assets/opp-mechtest.png')" :small-width="500" />
        <image-viewer :src="require('@/assets/opp-mechload.png')" :small-width="500" />
        <p>
          In the next line, we can select the solver to be used. I just leave OpenSMOKE++ selected.
          Then I set the thread limit to the number of CPU cores to be used in my computer.
          We can read the xml files downloaded from ReSpecTh.hu, if we click on the green add button.
        </p>
        <image-viewer :src="require('@/assets/opp-load-xml.png')" :small-width="500" />
        <p>
          I have downloaded three xml files and I will use all the three.
        </p>
        <image-viewer :src="require('@/assets/opp-load-xml-dialog.png')" :small-width="500" />
        <p>
          The first file (x00002015.xml) is a jet stirred reactor measurement, the second file (x10002069.xml) is ignition delay measurement in shock tube and
          the third one (x30002031.xml) contains concentration time profile measured in flow reactor.
          If we double click on the name of the xml file, a new window will pop up, where we can get some information about the data stored in the file.
          In the points list (1), we can select which points will be simulated.
          In the next table (2), we can specify the standard deviations of the experiments for each data point separately.

        </p>
        <image-viewer :src="require('@/assets/opp-xml-view.png')" :small-width="500" />
        <p>
          I have collected the standard deviations in the following table, so you can just copy and paste the values into the <code>Uncertainties</code> (2) table in the xml details window.
          Do not forget to specify the correct scale and unit of standard deviations. It is absolute (<code>abs</code>) error for XMLs x00002015.xml and x30002031.xml
          with mole fraction unit and relative (<code>absln</code>) error for x10002069.xml.
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="px-12">
        <table width="200">
          <tr>
        <th colspan="2" class="text-center border-bottom">x00002015.xml</th>
          </tr>
          <tr><td>O<sub>2</sub></td><td>1.897E-04</td></tr>
          <tr><td>CO</td>	<td>8.178E-05</td></tr>
          <tr><td>CO<sub>2</sub></td>	<td>8.964E-05</td></tr>
          <tr><td>C<sub>2</sub>H<sub>4</sub></td>	<td>7.100E-08</td></tr>
          <tr><td>C<sub>2</sub>H<sub>6</sub></td>	<td>2.017E-07</td></tr>
          <tr><td>C<sub>2</sub>H<sub>2</sub></td>	<td>1.000E-07</td></tr>
          <tr><td>CH<sub>2</sub>O</td>	<td>4.604E-05</td></tr>
          <tr><td>CH<sub>3</sub>OH</td>	<td>9.529E-05</td></tr>
          <tr><td>H<sub>2</sub></td>	<td>9.778E-05</td></tr>
          <tr><td>CH<sub>4</sub></td>	<td>1.131E-06</td></tr>
        </table>
      </v-col>
      <v-col>
       <table width="200">
          <tr>
        <th colspan="2" class="text-center border-bottom">x10002069.xml</th>
          </tr>
        <tr><td>ignition delay</td><td>10.0%</td></tr>
       </table>
      </v-col>
      <v-col>
        <table width="200">
          <tr>
            <th colspan="2" class="text-center border-bottom">x30002031.xml</th>
          </tr>
          <tr><td>CH<sub>3</sub>OH</td>	<td>2.960E-04</td></tr>
          <tr><td>O<sub>2</sub></td>	<td>5.790E-04</td></tr>
          <tr><td>CO<sub>2</sub></td>	<td>2.400E-04</td></tr>
          <tr><td>CO</td>	<td>2.710E-04</td></tr>
          <tr><td>H<sub>2</sub></td>	<td>1.800E-04</td></tr>
          <tr><td>H<sub>2</sub>O</td>	<td>6.640E-04</td></tr>
          <tr><td>CH<sub>2</sub>O</td>	<td>5.350E-05</td></tr>
          <tr><td>HCOOH</td><td>1.840E-06</td></tr>
        </table>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <p>
          After that we can click on the start button to evaluate the calculations.
          Optima++ prompts for a filename where to save the input file.
        </p>
       <image-viewer :src="require('@/assets/opp-start-calc.png')" :small-width="500" />
        <p>
          The progress of the simulations can be followed in the <code>Run View</code> window. This view is also available
          after the Optima++ job finished using the <code>Run View</code> command in the <code>View</code> menu (or with <code class="shortcut">Ctrl + R</code> keyboard shortcut).
        </p>
       <image-viewer :src="require('@/assets/opp-running-calc.png')" :small-width="500" />
      </v-col>
    </v-row>
    <v-row class="my-5">
      <h1 id="v1-results">Viewing the results of mechanism testing</h1>
    </v-row>
    <v-row>
      <v-col>
        <p>
        Having all simulations finished, the results are immediately shown in a pop up window.
        </p>
       <image-viewer :src="require('@/assets/opp-results.png')" :small-width="500" />
        <p>
        The table in this window contains the experiment id, the point number, the name of the measured species, the measured data and the simulated results.
        We can jump to the next dataset in this table by clicking on the arrow button in the toolbar
          or by the <code class="shortcut">shift + down arrow</code> keyboard shortcut. We can copy the results to the system clipboard by clicking on the "copy" button in the toolbar.
          This way, the results are available for other applications.
        </p>
        <p>
          At the bottom right corner, you can check the number of successful/failed simulations. As you can see, we have 43 "failed" simulations.
          The reason is that experimental data are available for the concentrations of species C<sub>2</sub>H<sub>2</sub>, C<sub>2</sub>H<sub>4</sub> and HCOOH, but they are not included in the Li-2007 mechanism.
        </p>
        <p>
          The results can also be visualized by Optima++ using the <code>plots</code> tab.
        </p>
         <image-viewer :src="require('@/assets/opp-plots.png')" :small-width="500" />
        <p>
          The simulated concentration profiles and ignition delay times are in very good agreement with the experimental data.
          This can be quantified by calculating the error function value.
          This is automatically done by Optima++, if we provide the standard deviations in the input file and we can see it on the
          <code>Error function values</code> tab.
        </p>
       <image-viewer :src="require('@/assets/opp-errf.png')" :small-width="500" />
        <p>
          The calculated error function value for the three investigated xml files is around 6.0,
          which means that on average the simulated results are within 3 sigma errors of the experimental data.
        </p>
        <p>
          The error function values can be further investigated by clicking on the <code>By datasets</code> and the <code>By points</code> tabs.
        </p>
        <image-viewer :src="require('@/assets/opp-errf-point.png')" :small-width="500" />
        <p>
          In the last page we can also manually exclude or include selected data points,
          and the recalculated error function value is immediately shown at the bottom of the page.
        </p>
        <image-viewer :src="require('@/assets/opp-errf-point-excl.png')" :small-width="500" />
        <p>
          This was a demonstration that if the RKD-format file that contains the detailed description of the experiment and the experimental results is available,
          then testing a mechanism against the data set is very easy. Several hundred datasets can also be used for testing in a similar way in a single run.
          The testing procedure can also be repeated with other mechanisms and this way the performance of several mechanisms can be compared easily.
        </p>
      </v-col>
    </v-row>
    <v-row class="my-5">
      <h1 id="v1-references">References</h1>
    </v-row>
    <v-row>
      <v-col>
        <references :references="references"></references>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import primeLogo from '@/assets/prime-logo.png'
import ImageViewer from '@/components/ImageViewer'
import Citation from '@/components/Citation'
import References from '@/components/References'
import ExtraBits from '@/components/ExtraBits'
import OptimaInstall from '@/views/bits/OptimaInstall.md'
import DownloadBox from '@/components/DownloadBox'

export default {
  name: "Video1",
  components: {ExtraBits, Citation, ImageViewer, References, DownloadBox},
  data: () => ({
    primeLogo,
    optimaInstall: OptimaInstall,
    rkdExample:`
    <property name="initial composition"  sourcetype="reported" >
            <component><speciesLink preferredKey="CH3OH"  CAS="67-56-1"    InChI="1S/CH4O/c1-2/h2H,1H3"  SMILES="CO"   chemName="methanol" /><amount units="mole fraction">0.05</amount></component>
            <component><speciesLink preferredKey="O2"     CAS="7782-44-7"  InChI="1S/O2/c1-2"            SMILES="O=O"  chemName="oxygen"   /><amount units="mole fraction">0.15</amount></component>
            <component><speciesLink preferredKey="N2"     CAS="7727-37-9"  InChI="1S/N2/c1-2"            SMILES="N#N"  chemName="nitrogen" /><amount units="mole fraction">0.80</amount></component>
    </property>
    `,
    references: [
      {
        id: 'Frenklach2007',
        title: 'Transforming data into knowledge—Process Informatics for combustion chemistry',
        journal: 'Proceedings of the Combustion Institute',
        volume: '31',
        number: '1',
        pages: '125 - 140',
        year: '2007',
        issn: '1540-7489',
        doi: 'https://doi.org/10.1016/j.proci.2006.08.121',
        url: 'http://www.sciencedirect.com/science/article/pii/S1540748906003841',
        author: 'Michael Frenklach',
        logo: primeLogo
      },
      {
        id: 'RespecthSpec2_2',
        title: 'ReSpecTh Kinetics Data Format Specification v2.2',
        author: 'Tamás Varga and Carsten Olm and Ágota Busai and István Gy. Zsély',
        year: '2019',
        from: 'http://www.respecth.hu',
        download: '/tutorials/ReSpecTh_Kinetics_Data_Format_Specification_v2.2.pdf',
        downloadName: 'ReSpecTh Kinetics Data Format Specification v2.2',
        icon: 'mdi-file-pdf'
      },
      {
        id: 'RespecthSpec2_3',
        title: 'ReSpecTh Kinetics Data Format Specification v2.3',
        author: 'Tamás Varga and Carsten Olm and Máté Papp and Ágota Busai and István Gy. Zsély',
        year: '2020',
        from: 'http://www.respecth.hu',
        download: '/tutorials/ReSpecTh_Kinetics_Data_Format_Specification_v2.3.pdf',
        downloadName: 'ReSpecTh Kinetics Data Format Specification v2.3',
        icon: 'mdi-file-pdf'
      },
      {
        id: 'OptimaPP',
        title: 'Optima++: A general C++ framework for performing combustion simulations and mechanism optimization',
        author: 'Tamás Varga and Máté Papp and Ágota Busai and István Gy. Zsély',
        year: '2020',
        from: 'http://www.respecth.hu',
        logo: require('@/assets/opp-logo.png'),
        logoTitle: 'Optima++ package V1.1.2 (2020)'
      },
      {
        id: 'flamemaster',
        title: 'FlameMaster: A C++ Computer Program for 0D Combustion and 1D Laminar Flame Calculations',
        author: 'H. Pitsch',
        year: '2019',
        from: 'https://www.itv.rwth-aachen.de/downloads/flamemaster/',
        logoTitle: 'FlameMaster 4.2.1 (2019)'
      },
      {
        id: 'opensmoke',
        title: 'OpenSMOKE++: An object-oriented framework for the numerical modeling of reactive systems with detailed kinetic mechanisms',
        author: 'A. Cuoci and A. Frassoldati and T. Faravelli and E. Ranzi',
        journal: 'Comput Phys Commun',
        volume: '192',
        pages: '237&mdash;264',
        year: '2015',
        doi: 'http://dx.doi.org/10.1016/j.cpc.2015.02.014',
        from: 'https://www.opensmokepp.polimi.it/',
        logo: require('@/assets/opensmoke-logo.png'),
        logoTitle: 'OpenSMOKE++ 0.12.0 (2020)'
      }
    ]
  }),
  methods: {
    bookMarks () {
      return [
        {
          id: '#introduction',
          title: 'Introduction'
        },
        {
          id: '#v1-respecth',
          title: 'ReSpecTh'
        },
        {
          id: '#v1-download',
          title: 'Downloading data'
        },
        {
          id: '#v1-mechanism',
          title: 'Obtaining a mechanism'
        },
        {
          id: '#v1-optima',
          title: 'Running Optima++'
        },
        {
          id: '#v1-results',
          title: 'Analyzing results'
        }
      ]
    }
  }
}
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid;
}
.dash {
  border-left: 4px solid;
  border-color: #ff8f00;
}
</style>